type WakeLockSentinelLike = {
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
  removeEventListener: (type: "release", listener: () => void) => void;
};

let sentinel: WakeLockSentinelLike | null = null;
let wantLock = false;
let listening = false;

function onSentinelRelease() {
  sentinel = null;
  if (wantLock) void acquireWakeLock();
}

async function acquireWakeLock(): Promise<void> {
  if (!wantLock || sentinel) return;
  if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;
  if (typeof document === "undefined" || document.visibilityState !== "visible") return;

  try {
    const lock = await navigator.wakeLock.request("screen");
    if (!wantLock) {
      await lock.release();
      return;
    }
    sentinel = lock;
    lock.addEventListener("release", onSentinelRelease);
  } catch {
    // Unsupported, denied, or low battery — safe to ignore.
  }
}

async function releaseWakeLock(): Promise<void> {
  wantLock = false;
  if (!sentinel) return;

  const lock = sentinel;
  sentinel = null;
  lock.removeEventListener("release", onSentinelRelease);
  try {
    await lock.release();
  } catch {
    // Already released.
  }
}

function onVisibilityChange() {
  if (document.visibilityState === "visible" && wantLock) {
    void acquireWakeLock();
  }
}

/** Keep the device screen awake while the player is watching an active sequence. */
export function enableScreenWakeLock(): void {
  wantLock = true;
  if (!listening) {
    document.addEventListener("visibilitychange", onVisibilityChange);
    listening = true;
  }
  void acquireWakeLock();
}

export function disableScreenWakeLock(): void {
  if (listening) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    listening = false;
  }
  void releaseWakeLock();
}

export function isScreenWakeLockSupported(): boolean {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}
